﻿/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useMemo } from 'react';
import { Text, View } from 'react-native';
import { ticketColors } from '../../../shared/theme/ticketColors';
import type { PlanEntitlementsLike } from '../../../../../../shared/plan-benefits';
import { normalizePlanCode } from '../../../../../../shared/plan-benefits';
import {
  formatGrantExpiryLabel,
  planDisplayName,
  type ActivePlanGrant,
} from '../../../../../../shared/billing-catalog';
import { creceLegacyActivateCopy } from '../../../../../../shared/plan-crece-copy';
import { billingStyles as styles } from '../billingScreen.styles';
import { BillingPlanHighlights } from './BillingPlanHighlights';

type UsageLine = { line: string; value: string; overQuota: boolean; used: number; max: number | null } | null;
/** Línea de volumen del mes (p. ej. `Ventas este mes` + `726/250`). */
export type VolumeUsageLine = { label: string; value: string; overQuota: boolean; used: number; limit: number } | null;

/** Barra de uso: brand → warn (≥80%) → bad (≥100%). */
function UsageMeter({ label, used, max }: { label: string; used: number; max: number }) {
  const ratio = max > 0 ? Math.min(1, used / max) : 0;
  const fillColor = ratio >= 1 ? ticketColors.bad : ratio >= 0.8 ? ticketColors.warn : ticketColors.brandPrimary;
  return (
    <View style={styles.usageMeter}>
      <View style={styles.usageMeterHead}>
        <Text style={styles.usageMeterLabel}>{label}</Text>
        <Text style={styles.usageMeterValue}>
          {used}/{max}
        </Text>
      </View>
      <View style={styles.usageMeterTrack}>
        <View
          style={[styles.usageMeterFill, { width: `${Math.round(ratio * 100)}%`, backgroundColor: fillColor }]}
        />
      </View>
    </View>
  );
}

const CURRENT_PLAN_HIGHLIGHTS: Record<'gratis' | 'emprende' | 'crece' | 'legacy', string[]> = {
  gratis: ['1 sucursal', '1 usuario', 'productos sin límite', 'POS, MP QR, tienda online'],
  emprende: ['1 sucursal', '1 usuario', 'Más ventas, pedidos y turnos por mes'],
  crece: ['Multi-sucursal, equipo y análisis avanzado', 'Ventas, pedidos y turnos sin límite'],
  legacy: ['Multi-sucursal, equipo y análisis avanzado', 'Ventas, pedidos y turnos sin límite'],
};

type Props = {
  entitlements: PlanEntitlementsLike;
  displayName: string;
  branchUsage: UsageLine;
  userUsage: UsageLine;
  /** Uso de volumen del mes (ventas/pedidos/turnos) — solo plan gratis. */
  volumeUsage?: VolumeUsageLine;
  isEmployee: boolean;
  layoutWide: boolean;
  activeGrant?: ActivePlanGrant | null;
  /** Línea precio + canal bajo el nombre (p. ej. `$25.000 / mes · cobrado por Mercado Pago`). */
  priceLine?: string | null;
};

export function BillingCurrentPlanCard({
  entitlements,
  displayName,
  branchUsage,
  userUsage,
  volumeUsage = null,
  isEmployee,
  layoutWide: _layoutWide,
  activeGrant = null,
  priceLine = null,
}: Props) {
  const planCode = normalizePlanCode(entitlements.plan_code);
  const isLegacy = planCode === 'legacy';
  const highlights = useMemo(() => {
    if (planCode === 'gratis' || planCode === 'emprende' || planCode === 'crece' || planCode === 'legacy') {
      return CURRENT_PLAN_HIGHLIGHTS[planCode];
    }
    return ['Plan a medida según tu negocio'];
  }, [planCode]);

  const trialSubtitle = activeGrant
    ? `Prueba del plan · hasta ${formatGrantExpiryLabel(activeGrant.expires_at)}`
    : null;
  const subscriptionNotice = entitlements.subscription_expires_at
    ? entitlements.subscription_status === 'cancelled' || entitlements.subscription_status === 'canceled'
      ? `${planDisplayName(entitlements.plan_code)} queda activo hasta ${formatGrantExpiryLabel(entitlements.subscription_expires_at)}. Después pasás a Gratis; no se renueva.`
      : entitlements.subscription_status === 'paused'
        ? `Tenés acceso hasta ${formatGrantExpiryLabel(entitlements.subscription_expires_at)}. Revisá la suscripción con tu proveedor.`
        : null
    : null;

  return (
    <View style={styles.currentPlanCard}>
      <Text style={styles.currentPlanLabel}>Plan actual</Text>
      <Text style={styles.currentPlanName}>{displayName}</Text>
      {priceLine ? <Text style={styles.currentPlanPriceLine}>{priceLine}</Text> : null}
      {trialSubtitle ? <Text style={styles.currentPlanTrialHint}>{trialSubtitle}</Text> : null}
      {subscriptionNotice ? <Text style={styles.currentPlanTrialHint}>{subscriptionNotice}</Text> : null}

      {branchUsage || userUsage || volumeUsage ? (
        <View style={styles.usageMeters}>
          {branchUsage ? (
            branchUsage.max != null ? (
              <UsageMeter label="Sucursales" used={branchUsage.used} max={branchUsage.max} />
            ) : (
              <Text style={styles.currentPlanUsageItem}>
                Sucursales <Text style={styles.currentPlanUsageValue}>{branchUsage.value}</Text>
              </Text>
            )
          ) : null}
          {userUsage ? (
            userUsage.max != null ? (
              <UsageMeter label="Usuarios" used={userUsage.used} max={userUsage.max} />
            ) : (
              <Text style={styles.currentPlanUsageItem}>
                Usuarios <Text style={styles.currentPlanUsageValue}>{userUsage.value}</Text>
              </Text>
            )
          ) : null}
          {volumeUsage ? (
            <View testID="billing.volume-usage">
              <UsageMeter label={volumeUsage.label} used={volumeUsage.used} max={volumeUsage.limit} />
            </View>
          ) : null}
        </View>
      ) : null}

      <BillingPlanHighlights lines={highlights} />

      {isLegacy ? (
        <View style={styles.legacyBanner}>
          <Text style={styles.legacyBannerTitle}>{creceLegacyActivateCopy.badgeTitle}</Text>
          <Text style={styles.legacyBannerBody}>
            {creceLegacyActivateCopy.badgeBody} {creceLegacyActivateCopy.managePitch}
          </Text>
        </View>
      ) : null}

      {isEmployee ? (
        <Text style={styles.offersFootnote}>
          Solo el dueño de la cuenta puede comprar o cambiar el plan.
        </Text>
      ) : null}
    </View>
  );
}
